// Copyright © 2021 Move Closer
import { onBeforeUnmount, onMounted, ref } from '@vue/composition-api';
import { FALLBACK_IMG_BASE64 } from '../../bootstrap/Image/Image.config';
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export const abstractImageProps = {
    alt: {
        type: String,
        required: false,
        default: ''
    },
    height: {
        type: Number,
        required: false
    },
    isLazy: {
        type: Boolean,
        required: false,
        default: true
    },
    sizes: {
        type: String,
        required: false,
        default: ''
    },
    src: {
        type: String,
        required: true,
        default: FALLBACK_IMG_BASE64
    },
    srcset: {
        type: String,
        required: false,
        default: ''
    },
    width: {
        type: Number,
        required: false
    }
};
/**
 * @param props
 * @param ctx
 * @param fallbackImage - The URL for the fallback image.
 * @param [loadCb?] - Callback function that will be called
 *  when the image file has been successfully loaded.
 * @param [errorCb?] - Callback function that will be called
 *  when the image file fails to load.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export const useImage = (props, ctx, fallbackImage, loadCb, errorCb) => {
    const { emit } = ctx;
    /**
     * Determines whether the `<img>` element has been successfully loaded.
     */
    const isLoaded = ref(false);
    /**
     * The value for the `[alt]` attribute.
     */
    const imgAlt = ref(props.alt);
    let imgSrc = ref(fallbackImage);
    /**
     * The value for the `[src]` attribute.
     */
    if (props.src) {
        imgSrc = ref(props.src);
    }
    /**
     * Renders the fallback image (in case of the load error).
     */
    const showFallbackImage = () => {
        imgAlt.value = '';
        imgSrc.value = fallbackImage;
    };
    /**
     * Handles the @error event on the `<img>` element.
     */
    const onError = () => {
        emit('error');
        if (!props.isLazy || !props.src) {
            showFallbackImage();
        }
        if (typeof errorCb === 'function') {
            errorCb();
        }
    };
    /**
     * Handles the @load event on the `<img>` element.
     */
    const onLoad = () => {
        if (isLoaded.value === true) {
            return;
        }
        emit('load');
        isLoaded.value = true;
        if (typeof loadCb === 'function') {
            loadCb();
        }
    };
    const onLoaded = (event) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        if (imgSrc.value === event.src && event.state.loaded) {
            if (ctx.root.$Lazyload) {
                ctx.root.$Lazyload.$off('loaded', onLoaded);
            }
            onLoad();
        }
    };
    onMounted(() => {
        if (ctx.root.$Lazyload) {
            ctx.root.$Lazyload.$on('loaded', onLoaded);
        }
    });
    onBeforeUnmount(() => {
        if (ctx.root.$Lazyload) {
            ctx.root.$Lazyload.$off('loaded', onLoaded);
        }
    });
    return {
        imgAlt,
        imgSrc,
        isLoaded,
        onError,
        onLoad
    };
};
